import styles from './PageTransition.module.scss'
import { AnimatePresence, motion } from 'framer-motion'
import { useRouter } from 'next/router'
import classNames from 'classnames'
import { TRANSITION_DURATION } from '@/data'
import useStore from '@/store'
import { useContext, useEffect, useRef, useState } from 'react'
import { ScrollContext } from '@/context/Scroll'
import gsap from 'gsap'
import useCurrentPage from '@/hooks/use-current-page'
import useBreakpoint, { getIsMobile } from '@/hooks/use-breakpoint'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const ANIMATION_DEFAULT = {
  name: 'default',
  start: {
    opacity: 0.99,
    transition: { duration: TRANSITION_DURATION, delay: 0 },
  },
  enter: {
    opacity: 1,
  },
  exit: {
    opacity: 0.98,
    transition: { duration: 0, delay: TRANSITION_DURATION },
  },
}

const ANIMATION_PRODUCT_PAGE = {
  name: 'product',
  // start: {
  //   opacity: 0.99,
  //   transition: { duration: 0.1, delay: 0 },
  // },
  // enter: {
  //   opacity: 1,
  //   transition: { duration: 0.1, delay: 0 },
  // },
  // exit: {
  //   opacity: 0.98,
  //   transition: { duration: 0.05, delay: 0.1 },
  // },
  start: {
    opacity: 0.99,
    transition: { duration: TRANSITION_DURATION, delay: 0 },
  },
  enter: {
    opacity: 1,
  },
  exit: {
    opacity: 0.98,
    transition: { duration: 0, delay: TRANSITION_DURATION },
  },
}

const formatRouterPath = path => {
  return path === '/index' ? '/' : path
}

const PAGE_CLASS = styles.page
const PAGE_INNER_CLASS = styles.page__inner
const OVERLAY_CLASS = styles.overlay
const MASK_CLASS = styles.mask

const PageTransition = ({ className, children }) => {
  const router = useRouter()
  const setPageIsTransitioning = useStore(state => state.setPageIsTransitioning)
  const setNavIsOpen = useStore(state => state.setNavIsOpen)
  const { scroll } = useContext(ScrollContext)
  const prevPathRef = useRef(null)
  const { prevPathIsProductInnerPage, isProductInnerPage } = useCurrentPage()
  const childrenTimeoutRef = useRef(null)
  const childrenTimeoutInnerRef = useRef(null)
  const [animationType, setAnimationType] = useState(ANIMATION_DEFAULT)
  const prevPath = useStore(state => state.prevPath)
  const setForceEnableScrollDisabler = useStore(state => state.setForceEnableScrollDisabler)
  const { isMobile } = useBreakpoint()
  const [pathAndChildren, setPathAndChildren] = useState({
    path: router.asPath,
    children: children,
  })

  useEffect(() => {
    if (childrenTimeoutRef.current) {
      clearTimeout(childrenTimeoutRef.current)
    }

    if (childrenTimeoutInnerRef.current) {
      clearTimeout(childrenTimeoutInnerRef.current)
    }

    let animationType = ANIMATION_DEFAULT

    if (isProductInnerPage && !isMobile) {
      if (prevPathIsProductInnerPage || !prevPath) {
        animationType = ANIMATION_PRODUCT_PAGE
      }
    }

    childrenTimeoutRef.current = setTimeout(() => {
      setAnimationType(animationType)
      childrenTimeoutInnerRef.current = setTimeout(() => {
        setPathAndChildren({
          path: formatRouterPath(router.asPath), // fix for prod...
          children: children,
        })
      }, 50)
    }, 50)
  }, [children, prevPathIsProductInnerPage, isProductInnerPage, prevPath, router.asPath, isMobile])

  const getOldPage = () => {
    const $el = document.querySelector(`.${PAGE_CLASS}[data-path="${prevPathRef.current}"]`)
    if (!$el) return
    return $el
  }

  const getNewPage = () => {
    const $el = document.querySelector(`.${PAGE_CLASS}:not([data-path="${prevPathRef.current}"])`)
    if (!$el) return
    return $el
  }

  const getTransitionPages = () => {
    if (typeof window === 'undefined') {
      return {
        oldPage: null,
        newPage: null,
      }
    }
    return {
      oldPage: getOldPage(),
      newPage: getNewPage(),
    }
  }

  const transitionOutOldPage = () => {
    const { oldPage: $el } = getTransitionPages()

    if (!$el) return
    const $overlay = $el.querySelector(`.${OVERLAY_CLASS}`)
    gsap.killTweensOf($overlay)
    gsap.to($overlay, {
      opacity: 0.5,
      duration: TRANSITION_DURATION,
    })

    let y = window.innerWidth * 0.09 * -1
    if (getIsMobile(window.innerWidth)) {
      y = window.innerWidth * 0.3 * -1
    }

    const tween = gsap.to($el, {
      y,
      duration: TRANSITION_DURATION,
      ease: 'Power4.easeInOut',
      onUpdate: () => {
        // Gotta hide this shit before the transition ends
        // or else there's a weird flash going between pages
        if (tween.progress() >= 0.98) {
          gsap.set($el, {
            position: 'absolute',
            height: 1,
            width: 1,
            top: 0,
            left: 0,
            opacity: 0,
            overflow: 'hidden',
          })
        }
      },
    })
  }

  const transitionInNewPage = onComplete => {
    const { newPage: $el } = getTransitionPages()
    if (!$el) return
    gsap.killTweensOf($el)
    const $mask = $el.querySelector(`.${MASK_CLASS}`)
    gsap.to($mask, {
      '--y': 0,
      duration: TRANSITION_DURATION,
      ease: 'Power4.easeInOut',
      onComplete: () => {
        if (onComplete) onComplete()
      },
    })
  }

  const pagesExistCheck = () => {
    return new Promise((resolve, reject) => {
      const int = setInterval(() => {
        const { oldPage, newPage } = getTransitionPages()
        if (!oldPage || !newPage) {
          return
        }
        clearInterval(int)
        resolve(true)
      }, 2)

      setTimeout(() => {
        clearInterval(int)
        reject(false)
      }, 4000)
    })
  }

  const transitionPagesAnimation = async onComplete => {
    try {
      await pagesExistCheck()
      transitionOutOldPage()
      transitionInNewPage(onComplete)
    } catch (e) {
      console.error('pagesExistCheck failed: ', e)
      return
    }
  }

  const onEnter = () => {
    const $el = document.querySelector(`.${PAGE_CLASS}:not([data-path="${prevPathRef.current}"])`)

    if (!$el) return

    setTimeout(() => {
      setPageIsTransitioning(false)

      scroll?.scrollTo(0, {
        immediate: true,
      })
      window.scroll(0, 0)

      setTimeout(() => {
        scroll?.start()
        setForceEnableScrollDisabler(false)
        $el.dataset.pageIsActive = 'true'
        scroll?.resize()
        ScrollTrigger.refresh()
      }, 20)
    }, 10)

    prevPathRef.current = formatRouterPath(router.asPath)
  }

  useEffect(() => {
    onEnter()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={classNames(styles.PageTransition, className)}>
      <AnimatePresence
        mode="sync"
        initial={false}
      >
        <motion.div
          onAnimationStart={async state => {
            if (state === 'exit') {
              scroll?.stop()
              setPageIsTransitioning(true)
            }
            if (state === 'enter') {
              scroll?.stop()
              setNavIsOpen(false)

              try {
                await transitionPagesAnimation(() => {
                  onEnter()
                })
              } catch (e) {}
            }
          }}
          initial="start"
          animate="enter"
          exit="exit"
          variants={animationType}
          data-themed="background-color"
          key={pathAndChildren?.path}
          data-path={formatRouterPath(pathAndChildren?.path)}
          className={PAGE_CLASS}
          data-animation-type={animationType.name}
        >
          <div className={PAGE_INNER_CLASS}>
            <div className={MASK_CLASS}>{pathAndChildren.children}</div>
            <div className={OVERLAY_CLASS} />
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

PageTransition.displayName = 'PageTransition'

export default PageTransition
